import { cloneDeep } from 'lodash/lang'
import { selectDays } from 'lib/cart/services/selection'
import { findRetailCartProduct } from 'lib/cart/decorators/cartForm'

/**
@private
If there is only one location, returns that location to be pre-selected. Otherwise, returns null.
*/
const defaultLocation = locations =>
  locations.length === 1 ? locations[0] : null

/**
@private
Initialize a cart product for the given camper/session if the camper has an
existing plan that can have days added to it
**/
const buildRegisteredCartProduct = ({ camper, session, cartForm }) => {
  // find existing purchaseable plan for this session
  const plan = camper.plans.find(
    p => p.session.id === session.id && p.canPurchaseDays
  )

  // continue to the next initializer
  if (!plan) {
    return null
  }

  // build a cart product for this plan and return
  const { product, location, accountDiscount } = plan

  const cartProduct = {
    camper,
    session,
    product,
    location,
    accountDiscount,
    plan,
    days: 0,
    redeemablePasses: []
  }

  cartForm.cartProducts.push(cartProduct)
  return cartProduct
}

/**
@private
Initialize a cart product for the given camper/session if the camper does not
have an existing plan or saved cart product.
**/
const buildUnregisteredCartProduct = ({
  camper,
  session,
  camperSession,
  cartForm
}) => {
  // camper may have a non-retail plan, and we should use its location
  const plan = camper.plans.find(p => p.session.id === session.id)

  // build the cart product
  const { accountDiscount } = camperSession
  const { locations } = session
  const location = plan?.location || defaultLocation(locations)
  const product = null // can't select default product without a location selection
  const days = 0
  const redeemablePasses = []

  const cartProduct = {
    camper,
    session,
    accountDiscount,
    location,
    product,
    days,
    redeemablePasses
  }
  cartForm.cartProducts.push(cartProduct)

  return cartProduct
}

const setEditLocation = ({ cartProduct, camperSession, session }) => {
  const { registered } = camperSession
  const { locations } = session
  cartProduct.editLocation = !registered && locations.length > 1
}

/**
@public
Initializes cartForm state for the given campers and sessions, using an optional
existing saved cart.
**/
const initializeCartForm = ({ cart, campers, sessions }) => {
  // clone an existing cart or create a new cart
  const cartForm = cart ? cloneDeep(cart) : {}
  if (!cartForm.cartProducts) {
    cartForm.cartProducts = []
  }

  campers.forEach(camper => {
    const { camperSessions } = camper

    sessions.forEach(session => {
      const camperSession =
        camperSessions.find(cs => cs.session.id === session.id) || {}

      initializeRetailCartProduct({ camper, session, camperSession, cartForm })
      initializeRedeemablePassCartProducts({
        camper,
        session,
        camperSession,
        cartForm
      })
    })
  })

  return cartForm
}

const initializeRedeemablePassCartProducts = ({
  camper,
  session,
  camperSession,
  cartForm
}) => {
  // detect cartProducts for this camper and session whose product category is not retail
  const cartProducts = cartForm.cartProducts.filter(
    cartProduct =>
      cartProduct.camper.id === camper.id &&
      cartProduct.session.id === session.id &&
      cartProduct.redeemablePasses.length > 0
  )

  cartProducts.forEach(cartProduct => {
    setEditLocation({ cartProduct, camperSession, session })
    cartProduct.camperSession = camperSession
  })
}

/**
 * @private
 * initialize a CartProduct for the given CamperSession
 */
const initializeRetailCartProduct = ({
  camper,
  session,
  camperSession,
  cartForm
}) => {
  const cartProduct =
    findRetailCartProduct({ camper, session, cartForm }) ||
    buildRegisteredCartProduct({ camper, session, cartForm }) ||
    buildUnregisteredCartProduct({ camper, session, camperSession, cartForm })

  if (cartProduct) {
    setEditLocation({ cartProduct, camperSession, session })
    cartProduct.camperSession = camperSession
    cartProduct.plan ||= camper.plans.find(
      plan => plan.session.id === session.id && plan.canPurchaseDays
    )

    // hydrate the soft-close acknowledgment from the persisted cart product
    cartProduct.acknowledgedSoftClose = !!cartProduct.restrictionsAcceptedAt

    // Transform cartReservationDays from GraphQL to reservationDays format for client
    if (
      cartProduct.cartReservationDays &&
      cartProduct.cartReservationDays.length > 0
    ) {
      cartProduct.reservationDays = cartProduct.cartReservationDays.map(
        crd => ({
          locationDayId: crd.locationDay.id,
          date: crd.locationDay.date
        })
      )
      delete cartProduct.cartReservationDays
    }

    if (camperSession.unlimitedDays) {
      cartProduct.days = 0
    } else if (cartProduct.product) {
      selectDays(cartProduct.days, { camper, session, camperSession, cartForm })
    }
  }
}

export { initializeCartForm }
